// Service Requests — the admin side of the client booking portal. The service team sees every request
// a client submitted (vessel + contact + HIN + what they need) and decides on it: Approve, Approve &
// change the date, or Decline. Each decision can email the client a message the team edits first (the
// email goes to the request's own address). Recording the decision here doesn't schedule the work —
// that still happens on the Service page.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ServiceRequest, ServiceRequestStatus } from '@technohull/shared';
import { useDecideServiceRequest, useServiceRequests } from '../features/hooks.js';
import { useAuth } from '../auth/AuthContext.js';
import { Chip, ChipGroup, EmptyState, ErrorState, FilterBar, InlineError, LoadingState, PageHeader, Pill, Segmented } from '../components/ui.js';
import { IconRefresh } from '../components/icons.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { formatClientDate } from '../lib/dates.js';
import { useIsPhone } from '../lib/media.js';

const GROW_NATIVE = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('field-sizing', 'content');

// kind → the shared Pill colours, which carry a request's status on its card (the card has no coloured
// side rule). Both follow the theme.
const STATUS_META: Record<ServiceRequestStatus, { label: string; kind: string }> = {
  new: { label: 'New', kind: 'info' },
  approved: { label: 'Approved', kind: 'ok' },
  declined: { label: 'Declined', kind: 'bad' },
};
type Filter = ServiceRequestStatus | 'all';
type Action = 'approved' | 'declined';

// the message the client receives, pre-filled from the request and the chosen action — the team edits
// it before sending. Kept plain-text; the worker sends it as-is.
function defaultEmail(action: Action, reschedule: boolean, r: ServiceRequest, proposedDate: string): { subject: string; body: string } {
  const name = r.clientName || 'Customer';
  const sign = '\n\nKind regards,\nTECHNOHULL Service Team';
  if (action === 'declined') {
    return {
      subject: `Regarding your TECHNOHULL service request ${r.ref}`,
      body: `Dear ${name},\n\nThank you for your request for ${r.vesselName} (ref ${r.ref}). Unfortunately we are unable to proceed with it at this time.\n\nIf you have any questions, please contact us at info@technohull.com.${sign}`,
    };
  }
  if (reschedule) {
    // the client reads this, so the dates are written out in full ("9 October 2026"), not in a staff preference
    const when = proposedDate ? formatClientDate(proposedDate) : '[date]';
    const instead = r.preferredDate ? ` instead of your requested ${formatClientDate(r.preferredDate)}` : '';
    return {
      subject: `Your TECHNOHULL service request ${r.ref} is approved`,
      body: `Dear ${name},\n\nThank you for your request for ${r.vesselName} (ref ${r.ref}). We are pleased to confirm it has been approved. We would like to propose ${when} for the service${instead}. Please let us know if that works for you.${sign}`,
    };
  }
  return {
    subject: `Your TECHNOHULL service request ${r.ref} is approved`,
    body: `Dear ${name},\n\nThank you for your request for ${r.vesselName} (ref ${r.ref}). We are pleased to confirm it has been approved. Our team will be in touch shortly to arrange the details.${sign}`,
  };
}

interface Compose { id: string; action: Action; reschedule: boolean }
interface Form { note: string; proposedDate: string; sendEmail: boolean; subject: string; body: string }

export function ServiceRequests() {
  const { data, isLoading, error, refetch, isFetching } = useServiceRequests();
  const decide = useDecideServiceRequest();
  const { can } = useAuth();
  const canWrite = can('servicereq:write');
  const fmt = useFormatDate();
  const df = useDateFormatter();
  // request timestamps: date in the user's chosen format, plus the time (24h).
  const fmtDate = (iso: string) => df.dateTime(iso) || iso;

  const [filter, setFilter] = useState<Filter>('new');
  const [compose, setCompose] = useState<Compose | null>(null);
  const [form, setForm] = useState<Form>({ note: '', proposedDate: '', sendEmail: true, subject: '', body: '' });
  const [flash, setFlash] = useState<{ text: string; warn: boolean } | null>(null);
  // phone: call / email buttons on each card, the status filter as a segmented switch, Refresh as an icon
  // beside "?", and the message box grows with its text so the page keeps a single scroll
  const isPhone = useIsPhone();
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || GROW_NATIVE) return;
    if (!isPhone) { el.style.height = ''; return; }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [isPhone, form.body, compose]);

  const requests = useMemo(() => data?.requests ?? [], [data]);
  const counts = useMemo(() => {
    const c = { new: 0, approved: 0, declined: 0 } as Record<ServiceRequestStatus, number>;
    for (const r of requests) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [requests]);
  const shown = useMemo(
    () => (filter === 'all' ? requests : requests.filter((r) => r.status === filter)),
    [requests, filter],
  );

  const open = (r: ServiceRequest, action: Action, reschedule: boolean) => {
    const proposedDate = reschedule ? r.preferredDate || '' : '';
    const eml = defaultEmail(action, reschedule, r, proposedDate);
    setForm({ note: '', proposedDate, sendEmail: true, subject: eml.subject, body: eml.body });
    setCompose({ id: r.id, action, reschedule });
    setFlash(null);
    decide.reset();
  };
  const resetTemplate = (r: ServiceRequest) => {
    if (!compose) return;
    const eml = defaultEmail(compose.action, compose.reschedule, r, form.proposedDate);
    setForm((f) => ({ ...f, subject: eml.subject, body: eml.body }));
  };
  const submit = (r: ServiceRequest) => {
    if (!compose) return;
    decide.mutate(
      {
        id: r.id,
        status: compose.action,
        note: form.note.trim(),
        proposedDate: compose.reschedule ? form.proposedDate : '',
        sendEmail: form.sendEmail,
        emailSubject: form.sendEmail ? form.subject : '',
        emailBody: form.sendEmail ? form.body : '',
      },
      {
        onSuccess: (res) => {
          setCompose(null);
          setFlash({
            text: form.sendEmail
              ? res.emailed
                ? `Decision saved and emailed to ${r.email}.`
                : 'Decision saved, but the client email could not be sent (email may not be configured).'
              : 'Decision saved.',
            // a saved decision whose email failed is a partial success, not a plain one.
            warn: form.sendEmail && !res.emailed,
          });
        },
      },
    );
  };

  const TABS: Array<{ key: Filter; label: string; n?: number }> = [
    { key: 'new', label: 'New', n: counts.new },
    { key: 'approved', label: 'Approved', n: counts.approved },
    { key: 'declined', label: 'Declined', n: counts.declined },
    { key: 'all', label: 'All', n: requests.length },
  ];

  return (
    <>
      <PageHeader title="Service Requests">
        {isPhone ? (
          <button type="button" className="btn btn-sm sr-refresh" disabled={isFetching} onClick={() => void refetch()} aria-label={isFetching ? 'Refreshing…' : 'Refresh'} title="Refresh">
            <IconRefresh size={18} />
          </button>
        ) : (
          <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}>{isFetching ? 'Refreshing…' : 'Refresh'}</button>
        )}
      </PageHeader>

      {isPhone ? (
        // the four statuses as one full-width switch, each count under its label
        <div className="sr-seg">
          <Segmented
            value={filter}
            onChange={setFilter}
            ariaLabel="Show"
            options={TABS.map((t) => ({ value: t.key, label: <><span>{t.label}</span>{typeof t.n === 'number' && <span className="sr-seg-n">{t.n}</span>}</> }))}
          />
        </div>
      ) : (
        <FilterBar>
          <ChipGroup label="Show">
            {TABS.map((t) => (
              <Chip key={t.key} active={filter === t.key} onClick={() => setFilter(t.key)}>
                {t.label}{typeof t.n === 'number' ? ` (${t.n})` : ''}
              </Chip>
            ))}
          </ChipGroup>
        </FilterBar>
      )}

      {flash && (
        <div className={`sr-flash${flash.warn ? ' warn' : ''}`} role="status">
          {flash.text}
          <button type="button" className="sr-flash-x" aria-label="Dismiss" onClick={() => setFlash(null)}>×</button>
        </div>
      )}

      {isLoading && <LoadingState label="Loading service requests…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && shown.length === 0 && (
        <EmptyState
          title={filter === 'all' ? 'No requests yet' : `No ${STATUS_META[filter].label.toLowerCase()} requests`}
          hint={filter === 'all' ? 'Requests clients book through the service portal appear here.' : filter === 'new' ? 'No new requests waiting - nice.' : 'No requests with this status.'}
        />
      )}

      <div className="sr-list">
        {shown.map((r) => {
          const meta = STATUS_META[r.status];
          const composing = compose?.id === r.id;
          const busy = composing && decide.isPending;
          return (
            <div key={r.id} className="sr-card">
              <div className="sr-top">
                <span className="sr-ref">{r.ref}</span>
                <Pill kind={meta.kind}>{meta.label}</Pill>
                <span className="sr-when">{fmtDate(r.createdAt)}</span>
              </div>
              <div className="sr-vessel">{r.vesselName}</div>
              {/* phone: the client is one tap away (hidden above the PHONE tier, where the links below serve) */}
              {(r.phone || r.email) && (
                <div className="sr-contact">
                  {r.phone && <a className="btn" href={`tel:${r.phone}`}><span className="sr-contact-txt">Call {r.phone}</span></a>}
                  {r.email && <a className="btn" href={`mailto:${r.email}`}><span className="sr-contact-txt">Email</span></a>}
                </div>
              )}
              <div className="sr-grid">
                <div><span className="sr-k">Client</span>{r.clientName}</div>
                <div><span className="sr-k">Email</span><a href={`mailto:${r.email}`}>{r.email}</a></div>
                <div><span className="sr-k">Phone</span>{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : <span className="muted">-</span>}</div>
                <div><span className="sr-k">HIN</span>{r.hin || <span className="muted">-</span>}</div>
                {r.preferredDate && <div><span className="sr-k">Preferred</span>{fmt(r.preferredDate) || r.preferredDate}</div>}
              </div>
              {r.details && <div className="sr-details"><span className="sr-k">Needs</span><span>{r.details}</span></div>}

              {r.status === 'new' ? (
                canWrite && (composing ? (
                  <div className="sr-compose">
                    <div className="sr-compose-title">
                      {compose.action === 'declined' ? 'Decline request' : compose.reschedule ? 'Approve & change the date' : 'Approve request'}
                    </div>
                    {compose.reschedule && (
                      <label className="sr-field">
                        <span className="field-label">Proposed date</span>
                        <input
                          className="field"
                          type="date"
                          value={form.proposedDate}
                          onChange={(e) => {
                            const d = e.target.value;
                            // an untouched template follows the new date; a body the team edited is left alone.
                            setForm((f) => {
                              const was = defaultEmail(compose.action, true, r, f.proposedDate).body;
                              return { ...f, proposedDate: d, ...(f.body === was ? { body: defaultEmail(compose.action, true, r, d).body } : {}) };
                            });
                          }}
                        />
                      </label>
                    )}
                    <label className="sr-field">
                      <span className="field-label">Internal note (not emailed)</span>
                      <input className="field" placeholder="Kept on the request for the team" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                    </label>
                    <label className="svcx-toggle check-row" style={{ margin: '2px 0 6px' }}>
                      <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))} /> Email the client at {r.email}
                    </label>
                    {form.sendEmail && (
                      <>
                        <label className="sr-field">
                          <span className="field-label">Subject</span>
                          {isPhone ? (
                            // two lines on a phone so the whole subject reads; still a single line of text
                            <textarea
                              className="field sr-subject"
                              rows={2}
                              value={form.subject}
                              onChange={(e) => { const v = e.target.value.replace(/[\r\n]+/g, ' '); setForm((f) => ({ ...f, subject: v })); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                            />
                          ) : (
                            <input className="field" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
                          )}
                        </label>
                        {/* not a <label> wrapper: it would forward a click on "Message" to the reset button */}
                        <div className="sr-field">
                          <span className="field-label"><label htmlFor={`sr-msg-${r.id}`}>Message</label> <button type="button" className="sr-reset" onClick={() => resetTemplate(r)}>reset to template</button></span>
                          <textarea ref={bodyRef} id={`sr-msg-${r.id}`} className="field sr-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
                        </div>
                      </>
                    )}
                    {decide.isError && <InlineError error={decide.error} onDismiss={() => decide.reset()} />}
                    <div className="sr-compose-actions">
                      <button
                        className={`btn btn-sm ${compose.action === 'declined' ? 'btn-danger' : 'btn-primary'}`}
                        disabled={busy}
                        onClick={() => submit(r)}
                      >
                        {busy ? 'Saving…' : compose.action === 'declined' ? 'Decline & send' : 'Approve & send'}
                      </button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => { setCompose(null); decide.reset(); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="sr-actions">
                    <button className="btn btn-sm btn-primary" onClick={() => open(r, 'approved', false)}>Approve</button>
                    <button className="btn btn-sm" onClick={() => open(r, 'approved', true)}>Approve &amp; change date</button>
                    <button className="btn btn-sm btn-danger" onClick={() => open(r, 'declined', false)}>Decline</button>
                  </div>
                ))
              ) : (
                <div className="sr-decided muted">
                  {meta.label} by {r.decidedBy || 'staff'} · {fmtDate(r.decidedAt)}
                  {r.proposedDate ? ` · proposed ${fmt(r.proposedDate) || r.proposedDate}` : ''}
                  {r.adminNote ? ` - “${r.adminNote}”` : ''}
                  {r.clientEmailedAt ? <span className="sr-emailed"> · client emailed {fmtDate(r.clientEmailedAt)}</span> : ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
